import React, { useCallback, useEffect, useRef } from 'react';

const ReusablePopup = ({ success = null, failure = null, info = null, onClose }) => {
  const popupRef = useRef(null);

  // Handle Escape key to close popup
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Handle click outside to close popup
  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Focus popup when it opens
  useEffect(() => {
    if ((success || failure || info) && popupRef.current) {
      popupRef.current.focus();
    }
  }, [success, failure, info]);

  let type = '';
  let message = '';
  let icon = '';
  let iconColor = '';
  let headerColor = '';

  // Base theme color: rgb(103, 73, 54)
  const themeBase = 'rgb(103, 73, 54)';
  const themeDark = 'rgb(83, 53, 34)'; // Slightly darker for contrast

  if (success) {
    type = 'Success';
    message = success;
    icon = '✅';
    iconColor = 'text-green-600';
    headerColor = 'text-gray-800';
  } else if (failure) {
    type = 'Failure';
    message = failure;
    icon = '❌';
    iconColor = 'text-red-600';
    headerColor = 'text-gray-800';
  } else if (info) {
    type = 'Info';
    message = info;
    icon = 'ℹ️';
    iconColor = 'text-gray-600';
    headerColor = 'text-gray-800';
  } else {
    return null; // No message to show
  }

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6"
      style={{ zIndex: 1200 }}
      onClick={handleOverlayClick}
    >
      <div
        ref={popupRef}
        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto"
        role="dialog"
        aria-labelledby="popup-title"
        aria-describedby="popup-message"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-start">
          <span className={`text-2xl mr-4 ${iconColor}`}>{icon}</span>
          <div className="flex-1 min-w-0">
            <h2 id="popup-title" className={`text-lg font-bold ${headerColor} mb-4 break-words`}>
              {type}
            </h2>
            <p id="popup-message" className="text-gray-600 mb-6 break-words whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-[rgb(103,73,54)] text-white px-4 py-2 rounded-lg hover:bg-[rgb(139,95,61)] transition-colors duration-300 font-semibold"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReusablePopup;